import { useCallback, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import {
  skipToken,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from '@apollo/client/react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  LinkIcon,
  MailIcon,
  PhoneIcon,
  Plus,
  UserSearchIcon,
} from 'lucide-react';

import { gql } from '@graphql/__generated__';
import {
  type EnquiryCreateBuyerLeadFragment,
  type UserSelectFragment,
} from '@graphql/__generated__/graphql';
import {
  AddUserModal,
  type UserCreationData,
} from '@modules/_shared/components/AddUserModal';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { UserSelect } from '@modules/_shared/components/user-select/UserSelect';
import { formatPhone } from '@modules/_shared/utils/formatting';
import { useLocale } from '@shared/hooks/locale';

export const ENQUIRY_CREATE_BUYER_LEAD_FRAGMENT = gql(/* GraphQL */ `
  fragment EnquiryCreateBuyerLead on enquiries {
    id
    first_name
    last_name
    email_address
    phone_number
    raw_email_address
    raw_phone_number
    user_id
    lot_id
    buyer_lead_id
  }
`);

const CREATE_MISSING_BUYER_LEAD = gql(/* GraphQL */ `
  mutation CreateMissingBuyerLead($enquiryId: uuid!, $userId: uuid!) {
    create_enquiry_buyer_lead(enquiryId: $enquiryId, userId: $userId) {
      buyer_lead {
        id
      }
      enquiry {
        id
        user {
          id
          ...UserCard_user
        }
        buyer_lead_id
        buyer_lead {
          id
          created_at
          source
          user {
            id
            ...UserCard_user
          }
        }
      }
    }
  }
`);

const ATTACH_BUYER_LEAD = gql(/* GraphQL */ `
  mutation AttachBuyerLead(
    $enquiryId: uuid!
    $buyerLeadId: uuid!
    $userId: uuid!
  ) {
    update_enquiries_by_pk(
      pk_columns: { id: $enquiryId }
      _set: { buyer_lead_id: $buyerLeadId, user_id: $userId }
    ) {
      id
      buyer_lead_id
      buyer_lead {
        id
        created_at
        source
        user {
          id
          ...UserCard_user
        }
      }
    }
  }
`);

const GET_LISTING_BUYER_LEADS = gql(/* GraphQL */ `
  query GetListingBuyerLeads($lotId: uuid!) {
    buyer_leads(where: { lot_id: { _eq: $lotId } }) {
      id
      user {
        id
        first_name
        last_name
      }
    }
  }
`);

const FIND_MATCHING_USERS = gql(/* GraphQL */ `
  query FindMatchingUser($email: citext, $phoneNumber: String) {
    users(
      where: {
        is_deleted: { _eq: false }
        _or: [
          { emails: { email: { _ilike: $email } } }
          { phone_numbers: { number: { _eq: $phoneNumber } } }
        ]
      }
    ) {
      id
    }
  }
`);

type EnquiryCreateBuyerLeadProps = {
  enquiry: EnquiryCreateBuyerLeadFragment;
  onUserSelected: (userId: string) => void;
  onBuyerLeadChanged?: () => void;
};

const SearchUserDialog: React.FC<
  EnquiryCreateBuyerLeadProps & {
    open: boolean;
    onClose: () => void;
  }
> = ({ enquiry, onUserSelected, open, onClose }) => {
  const { t } = useLocale();
  const { data, loading } = useQuery(GET_LISTING_BUYER_LEADS, {
    skip: enquiry?.lot_id == null || !open,
    variables: { lotId: enquiry?.lot_id ?? '' },
  });

  const listingBuyerLeads = data?.buyer_leads.map(lead => lead.user.id) ?? [];

  return (
    <SlideDialog
      title={t('Search user')}
      open={open}
      onClose={onClose}
      maxWidth="xs"
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {t('Enquiry contact details:')}
        </Typography>
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
            }}
          >
            {`${enquiry?.first_name ?? ''} ${
              enquiry?.last_name ?? ''
            }`.trim() || t('Unknown')}
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            <PhoneIcon size={20} color={'var(--mui-palette-text-secondary)'} />
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {formatPhone(
                enquiry?.phone_number ?? enquiry?.raw_phone_number,
              ) || '-'}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            <MailIcon size={20} color={'var(--mui-palette-text-secondary)'} />
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {enquiry?.email_address ?? enquiry?.raw_email_address ?? '-'}
            </Typography>
          </Stack>
        </Stack>
        <UserSelect
          userId={enquiry?.user_id}
          loading={loading}
          autoFocus
          filters={
            listingBuyerLeads.length > 0
              ? { id: { _nin: listingBuyerLeads } }
              : undefined
          }
          onChange={value => {
            if (value != null) {
              onUserSelected(value);
            }
          }}
        />
      </Box>
    </SlideDialog>
  );
};

const CreateUserCard: React.FC<
  EnquiryCreateBuyerLeadProps & { buyerLeadCreating: boolean }
> = ({ enquiry, onUserSelected, buyerLeadCreating }) => {
  const { t } = useLocale();
  const { text } = useTheme();
  const [openUserModal, setOpenUserModal] = useState<
    'create' | 'search' | null
  >(null);

  const onUserModalClosed = useCallback(
    (newUser?: UserSelectFragment) => {
      setOpenUserModal(null);

      if (newUser != null) {
        onUserSelected(newUser.id);
      }
    },
    [onUserSelected],
  );

  const defaultUserValue = useMemo<UserCreationData>(
    () => ({
      firstName: enquiry.first_name ?? undefined,
      lastName: enquiry.last_name ?? undefined,
      email: enquiry?.email_address ?? enquiry?.raw_email_address ?? undefined,
      phone: enquiry?.phone_number ?? enquiry?.raw_phone_number ?? undefined,
    }),
    [enquiry],
  );

  if (buyerLeadCreating) {
    return (
      <Stack
        sx={{
          flexGrow: 1,
          justifyContent: 'center',
          alignItems: 'center',
          p: 3,
        }}
      >
        <CircularProgress disableShrink />
        <Typography variant="body2" sx={{ mt: 1 }}>
          {t('Creating buyer lead...')}
        </Typography>
      </Stack>
    );
  }

  return (
    <>
      <Stack sx={{ gap: 1.5 }}>
        <Alert severity="warning">
          {t(
            'No user attached to this enquiry, you need to attach one in order to create a buyer lead.',
          )}
        </Alert>
        <Stack
          direction="row"
          sx={{
            alignItems: 'stretch',
            gap: 1,
          }}
        >
          <Stack
            sx={{
              alignItems: 'center',
              gap: 1,
              minWidth: 0,
            }}
          >
            <Typography variant="caption" sx={text.ellipsis}>
              {t('Create a new user')}
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Plus />}
              sx={{ ...text.ellipsis, maxWidth: '100%' }}
              onClick={() => setOpenUserModal('create')}
            >
              <span css={{ minWidth: 0, ...text.ellipsis }}>
                {`${enquiry?.first_name ?? ''} ${
                  enquiry?.last_name ?? ''
                }`.trim()}
              </span>
            </Button>
          </Stack>
          <Divider orientation="vertical" sx={{ height: 'auto', flex: 1 }}>
            {t('OR')}
          </Divider>
          <Stack sx={{ alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={text.ellipsis}>
              {t('Search for an existing user')}
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<UserSearchIcon />}
              onClick={() => setOpenUserModal('search')}
            >
              {t('Search')}
            </Button>
          </Stack>
        </Stack>
      </Stack>
      <AddUserModal
        opened={openUserModal === 'create'}
        onClose={onUserModalClosed}
        user={defaultUserValue}
      />
      <SearchUserDialog
        enquiry={enquiry}
        onUserSelected={userId => {
          setOpenUserModal(null);
          onUserSelected(userId);
        }}
        open={openUserModal === 'search'}
        onClose={() => setOpenUserModal(null)}
      />
    </>
  );
};

const AttachBuyerLeadButton: React.FC<{
  enquiryId: string;
  buyerLead: {
    id: string;
    user: { id: string; first_name?: string | null; last_name?: string | null };
  };
  onError: (error: ErrorLike) => void;
  onAttached?: () => void;
}> = ({ enquiryId, buyerLead, onError, onAttached }) => {
  const [attachBuyerLead, { loading: attachBuyerLeadLoading }] = useMutation(
    ATTACH_BUYER_LEAD,
    {
      onError,
    },
  );

  return (
    <Button
      color="primary"
      variant="contained"
      loading={attachBuyerLeadLoading}
      startIcon={<LinkIcon />}
      onClick={() =>
        attachBuyerLead({
          variables: {
            enquiryId,
            buyerLeadId: buyerLead.id,
            userId: buyerLead.user.id,
          },
        }).then(result => {
          if (result.error == null) {
            onAttached?.();
          }
        })
      }
    >
      {`${buyerLead.user.first_name ?? ''} ${
        buyerLead.user.last_name ?? ''
      }`.trim()}
    </Button>
  );
};

export const EnquiryCreateBuyerLead: React.FC<EnquiryCreateBuyerLeadProps> = ({
  enquiry,
  onUserSelected,
  onBuyerLeadChanged,
}) => {
  const { t } = useLocale();
  const { data: listingBuyerLeadsData, error: listingBuyerLeadsError } =
    useSuspenseQuery(
      GET_LISTING_BUYER_LEADS,
      enquiry.lot_id == null || enquiry.buyer_lead_id != null
        ? skipToken
        : {
            variables: {
              lotId: enquiry.lot_id ?? '',
            },
          },
    );

  const { data: matchingUsersData, error: matchingUsersError } =
    useSuspenseQuery(
      FIND_MATCHING_USERS,
      enquiry.lot_id == null || enquiry.buyer_lead_id != null
        ? skipToken
        : {
            variables: {
              email: enquiry.email_address ?? enquiry.raw_email_address ?? '',
              phoneNumber:
                enquiry.phone_number ?? enquiry.raw_phone_number ?? '',
            },
          },
    );

  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const [buyerLeadCreating, setBuyerLeadCreating] = useState(false);
  const [createBuyerLead] = useMutation(CREATE_MISSING_BUYER_LEAD, {
    onError: setErrorLike,
  });

  const onUserSelectedCallback = useCallback(
    async (userId: string) => {
      setBuyerLeadCreating(true);

      try {
        await createBuyerLead({
          variables: {
            enquiryId: enquiry.id,
            userId,
          },
          onCompleted: data => {
            if (data.create_enquiry_buyer_lead != null) {
              onUserSelected(userId);
              onBuyerLeadChanged?.();
            }
          },
        });
      } finally {
        setBuyerLeadCreating(false);
      }
    },
    [enquiry, createBuyerLead, onUserSelected, onBuyerLeadChanged],
  );

  const buyerLeadsToAttach = useMemo(() => {
    const matchingUsersIds =
      matchingUsersData?.users.map(user => user.id) ?? [];

    return (listingBuyerLeadsData?.buyer_leads ?? []).filter(lead =>
      enquiry.user_id != null
        ? lead.user.id === enquiry.user_id
        : matchingUsersIds.includes(lead.user.id),
    );
  }, [
    listingBuyerLeadsData?.buyer_leads,
    enquiry.user_id,
    matchingUsersData?.users,
  ]);

  const cardContent = useMemo(() => {
    if (
      enquiry.lot_id == null ||
      enquiry.buyer_lead_id != null ||
      listingBuyerLeadsError != null ||
      matchingUsersError != null
    ) {
      return null;
    }

    const userId = enquiry.user_id;

    if (buyerLeadsToAttach.length > 0) {
      return (
        <Stack sx={{ gap: 1.5 }}>
          <Alert severity="warning">
            {t(
              'No buyer lead created for this enquiry. We found matching buyer lead(s) for the listing. Attach one of them:',
            )}
          </Alert>
          <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
            {buyerLeadsToAttach.map(bl => (
              <AttachBuyerLeadButton
                key={bl.id}
                enquiryId={enquiry.id}
                buyerLead={bl}
                onError={setErrorLike}
                onAttached={onBuyerLeadChanged}
              />
            ))}
          </Stack>
        </Stack>
      );
    }

    if (userId == null) {
      return (
        <CreateUserCard
          enquiry={enquiry}
          onUserSelected={onUserSelectedCallback}
          buyerLeadCreating={buyerLeadCreating}
        />
      );
    }

    return (
      <Stack sx={{ gap: 1.5 }}>
        <Alert severity="warning">
          {t('No buyer lead created for this enquiry. Use the existing user:')}
        </Alert>
        <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
          <Button
            color="primary"
            variant="contained"
            loading={buyerLeadCreating}
            startIcon={<Plus />}
            onClick={() => onUserSelectedCallback(userId)}
          >
            {t('Create buyer lead')}
          </Button>
        </Stack>
      </Stack>
    );
  }, [
    t,
    buyerLeadsToAttach,
    enquiry,
    onUserSelectedCallback,
    buyerLeadCreating,
    listingBuyerLeadsError,
    matchingUsersError,
    onBuyerLeadChanged,
  ]);

  if (enquiry.lot_id == null || enquiry.buyer_lead_id != null) {
    return null;
  }

  return (
    <Box>
      <Stack sx={{ gap: 1.5 }}>
        {listingBuyerLeadsError != null && (
          <Alert severity="error">
            {t('Error loading listing buyer leads:')}{' '}
            {listingBuyerLeadsError.message}
          </Alert>
        )}
        {matchingUsersError != null && (
          <Alert severity="error">
            {t('Error finding matching users:')} {matchingUsersError.message}
          </Alert>
        )}
        {cardContent}
      </Stack>
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </Box>
  );
};
